import { C, cardRules } from "@bd/content";
import { children, dateOf } from "@bd/sim";
import { Icon, Meter, Sheet, Sparkline } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { t } from "../i18n";

/** The ← in a sub-screen's title: back to the Games screen (the × closes everything). */
export function BackTitle({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <span className="sub-title">
      <button className="icon-btn sub-back" onClick={onBack} aria-label={t("Back")} title={t("Back")} data-testid="sub-back"><Icon name="back" size={20} /></button>
      <span>{title}</span>
    </span>
  );
}

/** The tournament meta of a card-game line: win rates by deck, health, the ban list, and rotation. */
export function MetaScreen({ productId }: { productId: string }) {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const co = me(game)!;
  const root = co.products.find((p) => p.id === productId);
  if (!root) return null;
  const meta = root.meta;
  const sets = [root, ...children(co, root, "card_set")];
  const cards = sets.flatMap((s) => (s.cards ?? []).map((c) => ({ ...c, set: s })));
  const run = async (c: Parameters<typeof cmd>[0], ok: string) => {
    const r = await cmd(c);
    if (!r.ok) toast({ title: t("Not possible"), text: r.error, tone: "warn" });
    else toast({ title: ok, tone: "info" });
  };
  const solved = (meta?.dominance ?? 0) > 0.62;
  const last = meta ? t("Last tournament season {date}", { date: `${t(dateOf(meta.lastRun).season)} ${dateOf(meta.lastRun).year}` }) : "";
  return (
    <Sheet title={<BackTitle title={t("{name}: the meta", { name: root.design.name })} onBack={() => set({ screen: { kind: "products" } })} />} onClose={() => set({ screen: null })} wide testId="meta">
      {!meta && <p className="muted">{t("No tournaments yet")}</p>}
      {meta && (
        <div className="meta-grid">
          <div className="stack" style={{ gap: 6 }} title={last}>
            <span className="caps">{t("Win rate")}</span>
            {cardRules.archetypes.map((a) => (
              <div key={a.id} className="row" style={{ gap: 8 }} title={a.desc}>
                <span style={{ width: 80 }}>{t(a.name)}</span>
                <Meter value={meta.winRates[a.id] ?? 0} color={a.id === meta.top && solved ? "var(--bad)" : "var(--teal)"} style={{ flex: 1 }} label={t("{deck} win rate", { deck: t(a.name) })} />
                <span className="num" style={{ width: 40, textAlign: "right" }}>{Math.round((meta.winRates[a.id] ?? 0) * 100)}%</span>
              </div>
            ))}
            <span className={`tag ${solved ? "bad" : "good"} meta-verdict`}>{solved ? <><Icon name="warn" size={12} /> {t("Solved: {deck} wins too often.", { deck: t(C.archetype[meta.top].name) })}</> : <><Icon name="check" size={12} /> {t("Healthy")}</>}</span>
          </div>
          <div className="stack" style={{ gap: 6 }}>
            <span className="caps">{t("Health")}</span>
            <div className="row" style={{ gap: 8 }} title={t("Card sets reach {pct}% of their possible players.", { pct: Math.round(meta.health * 100) })}>
              <Meter value={meta.health} color={meta.health > 0.7 ? "var(--green)" : meta.health > 0.45 ? "var(--warn)" : "var(--bad)"} style={{ flex: 1 }} label={t("Meta health")} />
              <span className="num" style={{ width: 40, textAlign: "right" }}>{Math.round(meta.health * 100)}%</span>
            </div>
            <div className="row" style={{ gap: 8 }} title={t("Top deck's win rate, last two years")}>
              <Icon name="trend" size={16} color="var(--muted)" />
              <Sparkline data={meta.history.map((h) => h.dominance * 100)} w={160} />
            </div>
            <button className="btn small" style={{ alignSelf: "flex-start" }} onClick={() => run({ type: "rotateSet", productId: root.id }, t("Oldest set rotated out"))} title={t("Takes the oldest set out of tournaments.")} data-testid="rotate"><Icon name="replay" size={14} />{t("Rotate")}</button>
          </div>
        </div>
      )}
      <table className="list meta-cards" style={{ marginTop: 14 }}>
        <thead><tr><th>{t("Card")}</th>{sets.length > 1 && <th>{t("Set")}</th>}<th>{t("Deck")}</th><th>{t("Power")}</th><th /></tr></thead>
        <tbody>
          {cards.map((c) => (
            <tr key={c.id} data-testid="card-row">
              <td>
                <strong>{c.name}</strong>
                <span className="muted meta-kw"> {t(C.keyword[c.keyword]?.name ?? "")}{c.answer ? <span title={t("answers {deck}", { deck: t(C.archetype[c.answer].name) })}> · {t("vs {deck}", { deck: t(C.archetype[c.answer].name) })}</span> : null}</span>
              </td>
              {sets.length > 1 && <td>{c.set.design.name}{c.set.rotated ? ` ${t("(rotated)")}` : ""}</td>}
              <td>{t(C.archetype[c.archetype]?.name ?? "")}</td>
              <td className="num">{c.power}</td>
              <td style={{ textAlign: "right" }}>{c.banned ? <span className="bad">{t("Banned")}</span> : <button className="btn small ghost" onClick={() => run({ type: "banCard", cardId: c.id }, t("{card} banned", { card: c.name }))} data-testid="ban">{t("Ban")}</button>}</td>
            </tr>
          ))}
          {cards.length === 0 && <tr><td colSpan={5} className="muted">{t("No headline cards yet.")}</td></tr>}
        </tbody>
      </table>
    </Sheet>
  );
}
